import * as React from 'react'
import { Theme } from '@kit/ui'
import type { ThemeName } from 'tamagui'

const THEME_STORAGE_KEY = 'firma-digital-soe-web-theme'
const MODE_PREF_STORAGE_KEY = 'firma-digital-soe-mode-preference'
const COLOR_FAMILY_STORAGE_KEY = 'firma-digital-soe-color-family'

export type ThemeModePreference = 'light' | 'dark' | 'system'
export type AppThemeMode = ThemeName

const BASE_SCHEMES = ['light', 'dark'] as const
const COLOR_FAMILIES = [
  'none',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'red',
  'gray',
  'bronze',
  'gold',
] as const
const SCHEME_VARIANTS = ['alt1', 'alt2', 'active', 'surface1', 'surface2', 'surface3', 'surface4'] as const

export type ThemeOption = {
  value: ThemeName
  label: string
}

function titleCaseThemeName(value: string) {
  return value
    .replace(/_/g, ' ')
    .replace(/([a-z])([0-9])/gi, '$1 $2')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export const THEME_OPTIONS: ThemeOption[] = [
  ...BASE_SCHEMES.map((scheme) => ({
    value: scheme,
    label: titleCaseThemeName(scheme),
  })),
  ...BASE_SCHEMES.flatMap((scheme) =>
    COLOR_FAMILIES.filter((c) => c !== 'none').map((colorFamily) => ({
      value: `${scheme}_${colorFamily}` as ThemeName,
      label: `${titleCaseThemeName(scheme)} ${titleCaseThemeName(colorFamily)}`,
    }))
  ),
  ...BASE_SCHEMES.flatMap((scheme) =>
    SCHEME_VARIANTS.map((variant) => ({
      value: `${scheme}_${variant}` as ThemeName,
      label: `${titleCaseThemeName(scheme)} ${titleCaseThemeName(variant)}`,
    }))
  ),
  ...BASE_SCHEMES.flatMap((scheme) =>
    COLOR_FAMILIES.filter((c) => c !== 'none').flatMap((colorFamily) =>
      SCHEME_VARIANTS.map((variant) => ({
        value: `${scheme}_${colorFamily}_${variant}` as ThemeName,
        label: `${titleCaseThemeName(scheme)} ${titleCaseThemeName(colorFamily)} ${titleCaseThemeName(variant)}`,
      }))
    )
  ),
]

interface AppThemeContextValue {
  theme: AppThemeMode
  setTheme: React.Dispatch<React.SetStateAction<AppThemeMode>>
  toggleTheme: () => void
  modePreference: ThemeModePreference
  setModePreference: (mode: ThemeModePreference) => void
  colorFamily: string
  setColorFamily: (family: string) => void
}

const AppThemeContext = React.createContext<AppThemeContextValue | null>(null)

function getStoredModePref(): ThemeModePreference {
  if (typeof window !== 'undefined') {
    const storedMode = window.localStorage.getItem(MODE_PREF_STORAGE_KEY)
    if (storedMode === 'light' || storedMode === 'dark' || storedMode === 'system') {
      return storedMode
    }
  }
  return 'system'
}

function getStoredColorFamily(): string {
  if (typeof window !== 'undefined') {
    const storedFamily = window.localStorage.getItem(COLOR_FAMILY_STORAGE_KEY)
    if (storedFamily && COLOR_FAMILIES.includes(storedFamily as any)) {
      return storedFamily
    }

    const t = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (t) {
      if (t === 'light' || t === 'dark') return 'none'
      const parts = t.split('_')
      if (parts.length > 1 && COLOR_FAMILIES.includes(parts[1] as any)) {
        return parts[1]
      }
    }
  }
  return 'none'
}

interface AppThemeProviderProps {
  children: React.ReactNode
  systemScheme?: string | null
}

export function AppThemeProvider({ children, systemScheme }: AppThemeProviderProps) {
  const [modePreference, setModePreferenceState] = React.useState<ThemeModePreference>(getStoredModePref)
  const [colorFamily, setColorFamilyState] = React.useState<string>(getStoredColorFamily)
  const [systemIsDark, setSystemIsDark] = React.useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return systemScheme === 'dark'
  })

  // Escuchar cambios en la preferencia del sistema operativo
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemIsDark(e.matches)
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Determinar esquema efectivo ('light' u 'dark')
  const effectiveMode = React.useMemo(() => {
    if (modePreference === 'system') {
      return systemIsDark ? 'dark' : 'light'
    }
    return modePreference
  }, [modePreference, systemIsDark])

  // Nombre de tema efectivo (ej. 'light', 'dark', 'light_blue', 'dark_blue')
  const theme = React.useMemo<AppThemeMode>(() => {
    if (!colorFamily || colorFamily === 'none') {
      return effectiveMode as ThemeName
    }
    return `${effectiveMode}_${colorFamily}` as ThemeName
  }, [effectiveMode, colorFamily])

  // Guardar preferencia en localStorage y actualizar document
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(MODE_PREF_STORAGE_KEY, modePreference)
    window.localStorage.setItem(COLOR_FAMILY_STORAGE_KEY, colorFamily)
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
    document.documentElement.style.colorScheme = effectiveMode
  }, [modePreference, colorFamily, theme, effectiveMode])

  const setTheme = React.useCallback<React.Dispatch<React.SetStateAction<AppThemeMode>>>(
    (action) => {
      const newTheme = typeof action === 'function' ? action(theme) : action
      const [scheme, family] = newTheme.split('_')
      if (scheme === 'light' || scheme === 'dark') {
        setModePreferenceState(scheme)
      }
      if (family && COLOR_FAMILIES.includes(family as any)) {
        setColorFamilyState(family)
      } else if (!family && (scheme === 'light' || scheme === 'dark')) {
        setColorFamilyState('none')
      }
    },
    [theme]
  )

  const toggleTheme = React.useCallback(() => {
    setModePreferenceState((curr) => (curr === 'dark' ? 'light' : 'dark'))
  }, [])

  const setModePreference = React.useCallback((mode: ThemeModePreference) => {
    setModePreferenceState(mode)
  }, [])

  const setColorFamily = React.useCallback((family: string) => {
    setColorFamilyState(family)
  }, [])

  const value = React.useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      modePreference,
      setModePreference,
      colorFamily,
      setColorFamily,
    }),
    [theme, setTheme, toggleTheme, modePreference, setModePreference, colorFamily, setColorFamily]
  )

  return (
    <AppThemeContext.Provider value={value}>
      <Theme name={theme}>{children}</Theme>
    </AppThemeContext.Provider>
  )
}

export function useAppTheme() {
  const context = React.useContext(AppThemeContext)

  if (!context) {
    throw new Error('useAppTheme must be used within AppThemeProvider')
  }

  return context
}